import { useState } from 'react';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useChangeLogGate } from '../../../../shared/hooks/use-change-log-gate';
import {
  getDriveRootIdForDriveTab,
  getDriveRootIdForExplorer,
  getUserQuery,
} from '../../../../api/users-api';
import { useQuery } from '@tanstack/react-query';
import DriveGridWithTree from './drive-grid-with-tree';
import { useLocation, useParams } from 'react-router-dom';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { useStore } from '../../../../components/state/zustand-store';
import { Messages } from '../../../../utils/message-constants';
import { pagesConfig } from '../../../../shared/pages-config';
import {
  DriveRoles,
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import SharedDriveGridWithTree from './shared-drive-grid-with-tree';

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';

export default function DrivePage() {
  const { userEmail } = useParams() as { userEmail: string };
  const location = useLocation();
  const { changeLogRoles, primaryDomain, usersTier, driveRoles } =
    useUserSettings();
  const { setSnackbar } = useStore();

  const { data: user } = useQuery(getUserQuery(userEmail));

  // Determine entry point based on current route path
  // User Management context: /users/:userEmail/drive
  // Drive Explorer context: /drive-explorer or /drive-explorer?userEmail=...
  const isUserManagementContext =
    location.pathname.includes(`/${pagesConfig.users.link}/`) &&
    location.pathname.includes(`/${pagesConfig.userDetailsDrive.link}`);
  const isDriveExplorerContext = location.pathname.includes(
    `/${pagesConfig.driveExplorer.link}`
  );

  const [showSharedDrive, setShowSharedDrive] = useState(false);
  const [initialDriveRootId, setInitialDriveRootId] = useState<string | null>(
    null
  );

  const hasUserDriveTabChangeLogPermission = isAbleToSeeUIElement(
    changeLogRoles,
    UserRoles.ViewDriveTab.value
  );
  const hasDriveExplorerChangeLogPermission = isAbleToSeeUIElement(
    changeLogRoles,
    DriveRoles.ViewDriveExplorer.value
  );

  const userHasSharedDrivesPermission = isAbleToSeeUIElement(
    driveRoles,
    DriveRoles.ViewSharedDrives.value
  );

  // Determine permission based on current entry point
  const permissionValue = isUserManagementContext
    ? UserRoles.ViewDriveTab.value
    : DriveRoles.ViewDriveExplorer.value;

  const changeLogGate = useChangeLogGate({
    permissionValue,
    changeLogRoles,
    initialOpen: isUserManagementContext
      ? hasUserDriveTabChangeLogPermission
      : isDriveExplorerContext
      ? hasDriveExplorerChangeLogPermission
      : false,
  });

  // Determine permission name for overlay based on current context
  const getRequiredPermissionName = (): string => {
    if (isUserManagementContext) {
      return UserRoles.ViewDriveTab.name;
    }
    if (isDriveExplorerContext) {
      return DriveRoles.ViewDriveExplorer.name;
    }
    return pagesConfig.userDetailsDrive.name;
  };

  // Derive UI state from changelog gate
  const showEntryChangeLogDialog =
    changeLogGate.needsChangeLog && changeLogGate.openChangeLogDialog;
  const canRenderDriveContent = changeLogGate.canFetch;

  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!user) {
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        sx={{ width: '100%', minHeight: 300 }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  const canViewSharedDrives =
    usersTier.rank >= pagesConfig.sharedDrives.minimumTier;

  async function handleEntryChangeLogSubmit() {
    if (!userEmail || !changeLogGate.changeLogMessage.trim()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Use appropriate API endpoint based on entry point
      const rootId = isUserManagementContext
        ? await getDriveRootIdForDriveTab(
            userEmail,
            changeLogGate.changeLogMessage
          )
        : await getDriveRootIdForExplorer(
            userEmail,
            changeLogGate.changeLogMessage
          );

      // Validate that the API returned a valid root ID
      if (!rootId) {
        throw new Error('API returned an invalid root ID.');
      }

      setInitialDriveRootId(rootId);
      changeLogGate.confirm();
    } catch {
      setSnackbar({
        open: true,
        message: 'Failed to load drive data. Please try again.',
        severity: 'error',
      });
      // Reset to initial state on error
      changeLogGate.reset();
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <ChangeLogDialog
        open={showEntryChangeLogDialog}
        handleClose={changeLogGate.dismiss}
        changeLogMessage={changeLogGate.changeLogMessage}
        setChangeLogMessage={changeLogGate.setChangeLogMessage}
        mutate={handleEntryChangeLogSubmit}
        mutateLoading={isSubmitting}
      />
      <Tooltip
        arrow
        title={
          !canViewSharedDrives
            ? Messages.Errors.Tier.Viewing(
                pagesConfig.sharedDrives.minimumTierDisplayValue
              )
            : !userEmail
            ? 'Please enter a user email'
            : !userHasSharedDrivesPermission
            ? Messages.Errors.Permission.Viewing(
                DriveRoles.ViewSharedDrives.value
              )
            : ''
        }
      >
        <FormGroup sx={{ width: 'fit-content' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={showSharedDrive}
                onChange={(e) => setShowSharedDrive(e.target.checked)}
                disabled={
                  !canViewSharedDrives || !userHasSharedDrivesPermission
                }
              />
            }
            label="Shared Drives"
          />
        </FormGroup>
      </Tooltip>
      <Box sx={{ flex: 1, height: `calc(100% - 50px)` }}>
        {showSharedDrive ? (
          <SharedDriveGridWithTree
            key={userEmail}
            userEmail={userEmail}
            primaryDomain={primaryDomain}
            driveRoles={driveRoles}
            changeLogRoles={changeLogRoles}
          />
        ) : (
          <DriveGridWithTree
            userEmail={
              changeLogGate.needsChangeLog && !changeLogGate.canFetch
                ? ''
                : userEmail
            }
            primaryDomain={primaryDomain}
            driveRoles={driveRoles}
            changeLogRoles={changeLogRoles}
            initialDriveRootId={initialDriveRootId}
            canRenderDriveContent={canRenderDriveContent}
            onOpenChangeLogDialog={changeLogGate.openDialog}
            requiredPermissionName={getRequiredPermissionName()}
          />
        )}
      </Box>
    </>
  );
}
